// =============================================
// COMPONENTE: AuditLogViewer
// =============================================
// Arquivo: AuditLogViewer.tsx
// Descrição: Visualizador de logs de auditoria
// =============================================

import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, RefreshCw, Clock, User, Activity } from 'lucide-react';
import { apiService } from '../../services/api';
import './AuditLogViewer.css';

interface AuditLog {
  id: number;
  criado_em: string;
  usuario_nome: string;
  usuario_email: string;
  entidade_tipo: string;
  entidade_id: number;
  acao: string;
  descricao: string;
  ip_address: string;
}

interface Filters {
  entidade_tipo?: string;
  acao?: string;
  usuario_id?: number;
  data_inicio?: string;
  data_fim?: string;
  busca?: string;
}

const AuditLogViewer: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0
  });

  // =============================================
  // CARREGAR LOGS
  // =============================================

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const response = await apiService.getAutomationAuditLogs(params);
      if (response.success) {
        setLogs(response.data);
        setStats((response as any).stats || stats);
      }
    } catch (error) {
      // Erro tratado pelo apiService
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // =============================================
  // EXPORTAR LOGS
  // =============================================

  const handleExport = async () => {
    try {
      const params: Record<string, string> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value.toString();
      });

      const blob = await apiService.exportAutomationAuditLogs(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString()}.csv`;
      a.click();
    } catch (error) {
      // Erro tratado pelo apiService
    }
  };

  // =============================================
  // UTILITÁRIOS
  // =============================================

  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      create: '#48bb78',
      update: '#4299e1',
      delete: '#fc8181',
      execute: '#9f7aea',
      cancel: '#ed8936',
      duplicate: '#38b2ac'
    };
    return colors[action] || '#718096';
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      create: 'Criar',
      update: 'Atualizar',
      delete: 'Deletar',
      execute: 'Executar',
      cancel: 'Cancelar',
      duplicate: 'Duplicar'
    };
    return labels[action] || action;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <div className="audit-log-viewer">
      {/* HEADER */}
      <div className="audit-header">
        <div className="audit-title">
          <Activity size={24} />
          <h2>Log de Auditoria</h2>
        </div>

        <div className="audit-stats">
          <div className="stat-card">
            <Clock size={18} />
            <div className="stat-content">
              <span className="stat-value">{stats.today}</span>
              <span className="stat-label">Hoje</span>
            </div>
          </div>
          <div className="stat-card">
            <Activity size={18} />
            <div className="stat-content">
              <span className="stat-value">{stats.thisWeek}</span>
              <span className="stat-label">Esta Semana</span>
            </div>
          </div>
          <div className="stat-card">
            <User size={18} />
            <div className="stat-content">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total</span>
            </div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="audit-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Buscar nos logs..."
            value={filters.busca || ''}
            onChange={(e) => setFilters({ ...filters, busca: e.target.value })}
          />
        </div>

        <div className="toolbar-actions">
          <button
            className="btn-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            Filtros
          </button>
          <button
            className="btn-secondary"
            onClick={loadLogs}
            disabled={loading}
          >
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
            Atualizar
          </button>
          <button
            className="btn-primary"
            onClick={handleExport}
          >
            <Download size={18} />
            Exportar
          </button>
        </div>
      </div>

      {/* FILTROS */}
      {showFilters && (
        <div className="audit-filters">
          <div className="filter-group">
            <label>Tipo de Entidade</label>
            <select
              value={filters.entidade_tipo || ''}
              onChange={(e) => setFilters({ ...filters, entidade_tipo: e.target.value })}
            >
              <option value="">Todos</option>
              <option value="flow">Fluxo</option>
              <option value="template">Template</option>
              <option value="execution">Execução</option>
              <option value="variable">Variável</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Ação</label>
            <select
              value={filters.acao || ''}
              onChange={(e) => setFilters({ ...filters, acao: e.target.value })}
            >
              <option value="">Todas</option>
              <option value="create">Criar</option>
              <option value="update">Atualizar</option>
              <option value="delete">Deletar</option>
              <option value="execute">Executar</option>
              <option value="cancel">Cancelar</option>
              <option value="duplicate">Duplicar</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Data Início</label>
            <input
              type="date"
              value={filters.data_inicio || ''}
              onChange={(e) => setFilters({ ...filters, data_inicio: e.target.value })}
            />
          </div>

          <div className="filter-group">
            <label>Data Fim</label>
            <input
              type="date"
              value={filters.data_fim || ''}
              onChange={(e) => setFilters({ ...filters, data_fim: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* LOGS TABLE */}
      <div className="audit-logs">
        {loading ? (
          <div className="loading-state">
            <RefreshCw size={32} className="spinning" />
            <p>Carregando logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <Activity size={48} />
            <p>Nenhum log encontrado</p>
          </div>
        ) : (
          <table className="logs-table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Usuário</th>
                <th>Ação</th>
                <th>Tipo</th>
                <th>ID</th>
                <th>Descrição</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="log-date">{formatDate(log.criado_em)}</td>
                  <td className="log-user">
                    <div className="user-info">
                      <User size={14} />
                      <span>{log.usuario_nome || 'Sistema'}</span>
                    </div>
                  </td>
                  <td>
                    <span
                      className="log-action"
                      style={{ backgroundColor: getActionColor(log.acao) }}
                    >
                      {getActionLabel(log.acao)}
                    </span>
                  </td>
                  <td className="log-type">{log.entidade_tipo}</td>
                  <td className="log-id">#{log.entidade_id}</td>
                  <td className="log-description">{log.descricao}</td>
                  <td className="log-ip">{log.ip_address || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;
